import { useState } from 'react'
import { ArrowLeft, Trophy, HandCoins, BarChart3 } from 'lucide-react'
import { TeamCrest } from '@/components/TeamCrest'
import { StatusBadge } from '@/components/StatusBadge'
import { PlayedBadge } from '@/components/PlayedBadge'
import { RegisterBetModal } from '@/components/RegisterBetModal'
import { cn, formatOdds, probabilityDisplay } from '@/lib/utils'
import type { ChallengeHistoryItem } from '@/types/strategies'

function kickoffFullLabel(iso: string | null): string {
  if (!iso) return 'Prematch'
  return new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })
}

function FormDots({ form }: { form: string[] }) {
  if (form.length === 0) return <span className="text-[10px] text-text-dim italic">Sin historial suficiente</span>
  return (
    <div className="flex items-center gap-1">
      {form.map((r, i) => (
        <span
          key={i}
          className={cn(
            'h-2.5 w-2.5 rounded-full',
            r === 'W' && 'bg-success',
            r === 'L' && 'bg-danger',
            r === 'D' && 'bg-text-dim',
          )}
          title={r === 'W' ? 'Victoria' : r === 'L' ? 'Derrota' : 'Empate'}
        />
      ))}
    </div>
  )
}

/** P1.9F (25/09/2026, referencia visual aprobada -- "Reto - Detalle del pick"): pantalla
 * completa de un pick del Reto (Oficial o Mi Reto, semana en curso o histórica) -- misma
 * evidencia real que ya calculaba `compute_challenge_summary` para el último pick, ahora
 * disponible para CUALQUIERA (`get_challenge_history` enriquecido, Sección 3 del encargo).
 * "LA JUGUÉ" se conserva exactamente igual (Sección 2: "mantener LA JUGUÉ donde
 * corresponda"). */
export function PickDetailView({
  item, matchLabel, onBack,
}: {
  item: ChallengeHistoryItem
  matchLabel: string
  onBack: () => void
}) {
  const [showPlayModal, setShowPlayModal] = useState(false)
  const isNoBet = item.display_market == null
  const isSettled = item.status !== 'pendiente'
  const won = item.status === 'ganada'
  const probDisplay = probabilityDisplay(item.probability_used, item.probability_is_calibrated)

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-lg font-black text-text">Reto - Detalle del pick</h1>
          <p className="text-[10px] text-text-dim uppercase tracking-wider font-bold">John Anthony</p>
        </div>
      </div>

      {isNoBet ? (
        <div className="card p-6 text-center space-y-2">
          <Trophy className="h-8 w-8 mx-auto text-text-dim" />
          <p className="text-sm font-bold text-text">Sin oportunidad ese día</p>
          <p className="text-xs text-text-dim">John Anthony prefirió esperar -- no hubo ningún candidato con confianza suficiente.</p>
        </div>
      ) : (
        <>
          {isSettled && (
            <div
              className={cn(
                'rounded-xl p-2.5 flex items-center gap-2 border',
                won ? 'bg-success/10 border-success/40 text-success' : item.status === 'perdida' ? 'bg-danger/10 border-danger/40 text-danger' : 'bg-panel-elevated border-border/40 text-text-dim',
              )}
            >
              <span className="text-xs font-black uppercase tracking-wider">
                {won ? '✓ Acertó' : item.status === 'perdida' ? '✕ Falló' : 'Anulado'}
              </span>
              <span className="text-[10px] font-bold ml-auto">{kickoffFullLabel(item.step_date)}</span>
            </div>
          )}

          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-center gap-4 py-2">
              <div className="flex flex-col items-center gap-1.5 w-24 text-center">
                <TeamCrest src={item.home_team_logo} alt={item.home_team ?? 'Local'} size={48} />
                <span className="text-xs font-bold text-text line-clamp-1">{item.home_team ?? 'Local'}</span>
              </div>
              {item.actual_home_goals != null && item.actual_away_goals != null ? (
                <div className="text-center">
                  <p className="text-2xl font-mono font-black text-text">{item.actual_home_goals} - {item.actual_away_goals}</p>
                  <p className="text-[9px] text-text-dim uppercase font-bold">Resultado final</p>
                </div>
              ) : (
                <span className="text-lg font-mono font-black text-text-dim">VS</span>
              )}
              <div className="flex flex-col items-center gap-1.5 w-24 text-center">
                <TeamCrest src={item.away_team_logo} alt={item.away_team ?? 'Visitante'} size={48} />
                <span className="text-xs font-bold text-text line-clamp-1">{item.away_team ?? 'Visitante'}</span>
              </div>
            </div>

            <div className="bg-panel-elevated/40 rounded-xl p-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-[9px] font-bold uppercase text-text-dim">Selección del Reto</p>
                <p className="text-sm font-black text-text">{item.display_selection ?? matchLabel}</p>
                <p className="text-[10px] text-text-dim">{item.display_market}</p>
              </div>
              {item.odds != null && <p className="text-lg font-mono font-black text-brand shrink-0">{formatOdds(item.odds)}</p>}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-panel-elevated/30 rounded-lg p-2">
                <p className="text-[8px] font-bold uppercase text-text-dim">Probabilidad JA</p>
                <p className="text-sm font-mono font-black text-text">{probDisplay.kind === 'unknown' ? 'N/D' : probDisplay.label}</p>
              </div>
              <div className="bg-panel-elevated/30 rounded-lg p-2">
                <p className="text-[8px] font-bold uppercase text-text-dim">Confianza</p>
                <p className="text-sm font-black text-yellow-400">{item.confidence_tier === 'ULTRA_SAFE' ? 'Máxima' : item.confidence_tier === 'SAFE' ? 'Alta' : 'N/D'}</p>
              </div>
              <div className="bg-panel-elevated/30 rounded-lg p-2">
                <p className="text-[8px] font-bold uppercase text-text-dim">Riesgo</p>
                <p className="text-sm font-black text-text">{item.risk_score != null ? `${item.risk_score.toFixed(0)}/100` : 'N/D'}</p>
              </div>
            </div>

            {item.narrative && (
              <div className="bg-panel-elevated/30 border border-border/30 rounded-xl p-3">
                <p className="text-xs text-text-muted italic leading-relaxed">&ldquo;{item.narrative}&rdquo;</p>
              </div>
            )}

            <button className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-border/60 text-text-dim text-[10px] font-black uppercase tracking-wider">
              <BarChart3 className="h-3.5 w-3.5" /> Ver análisis completo
            </button>
          </div>

          <div className="card p-4 space-y-2.5">
            <p className="eyebrow">Resultado y banca</p>
            {item.actual_home_goals != null && item.actual_away_goals != null && (
              <div className="flex items-center justify-between">
                <span className="text-2xl font-mono font-black text-text">{item.actual_home_goals} - {item.actual_away_goals}</span>
                {isSettled && <StatusBadge status={item.status} />}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center pt-1">
              <div className="bg-panel px-2 py-1.5 rounded-lg border border-border/40">
                <p className="text-text-dim font-bold uppercase text-[8px]">Cuota jugada</p>
                <p className="font-mono font-black text-text text-xs">{item.odds != null ? formatOdds(item.odds) : 'N/D'}</p>
              </div>
              <div className="bg-panel px-2 py-1.5 rounded-lg border border-border/40">
                <p className="text-text-dim font-bold uppercase text-[8px]">Stake {item.stake_pct_used != null ? `(${(item.stake_pct_used * 100).toFixed(0)}%)` : ''}</p>
                <p className="font-mono font-black text-text text-xs">{item.stake.toFixed(2)}€</p>
              </div>
              <div className="bg-panel px-2 py-1.5 rounded-lg border border-border/40">
                <p className="text-text-dim font-bold uppercase text-[8px]">Banca antes → después</p>
                <p className="font-mono font-black text-text text-xs">
                  {item.bankroll_before.toFixed(2)}€{item.bankroll_after != null ? ` → ${item.bankroll_after.toFixed(2)}€` : ''}
                </p>
              </div>
            </div>
          </div>

          {(item.home_expected_goals != null || item.last5_home.length > 0 || item.last5_away.length > 0) && (
            <div className="card p-4 space-y-2.5">
              <p className="eyebrow">Datos del análisis</p>
              {(item.home_expected_goals != null || item.away_expected_goals != null) && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-panel-elevated/30 rounded-lg p-2">
                    <p className="text-[9px] font-bold uppercase text-text-dim">xG {item.home_team ?? 'local'}</p>
                    <p className="text-sm font-mono font-black text-text">{item.home_expected_goals?.toFixed(2) ?? 'N/D'}</p>
                  </div>
                  <div className="bg-panel-elevated/30 rounded-lg p-2">
                    <p className="text-[9px] font-bold uppercase text-text-dim">xG {item.away_team ?? 'visitante'}</p>
                    <p className="text-sm font-mono font-black text-text">{item.away_expected_goals?.toFixed(2) ?? 'N/D'}</p>
                  </div>
                </div>
              )}
              {(item.last5_home.length > 0 || item.last5_away.length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[9px] font-bold uppercase text-text-dim mb-1">Últimos 5 ({item.home_team ?? 'local'})</p>
                    <FormDots form={item.last5_home} />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase text-text-dim mb-1">Últimos 5 ({item.away_team ?? 'visitante'})</p>
                    <FormDots form={item.last5_away} />
                  </div>
                </div>
              )}
            </div>
          )}

          {item.step_id != null && (
            item.my_bet ? (
              <PlayedBadge bet={item.my_bet} />
            ) : (
              <button
                onClick={() => setShowPlayModal(true)}
                className="w-full flex items-center justify-center gap-1.5 py-3 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider transition active-scale"
              >
                <HandCoins className="h-3.5 w-3.5" /> La jugué
              </button>
            )
          )}

          {showPlayModal && (
            <RegisterBetModal
              onClose={() => setShowPlayModal(false)}
              category="challenge"
              matchLabel={matchLabel}
              selectionLabel={item.display_selection ?? item.selection ?? ''}
              referenceOdds={item.odds ?? 0}
              challengeStepId={item.step_id}
            />
          )}
        </>
      )}
    </div>
  )
}
